import type { Ref } from 'vue'
import { computed, unref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { QUERY_KEYS } from '@/constants/query'
import { TIME } from '@/constants/time'
import useMessaging from '@/composables/tithelyMessaging'

/**
 * The current org's Messaging-service record, from
 * `GET /v1/organizations/{org_id}`. Carries the messaging entitlement flags —
 * `messaging_enabled` and the per-channel `channels.sms.enabled` — used to gate
 * access to the Inbox. Cached for a few minutes; the query resolves its own Hub
 * connection, so it can run before any other Messaging call has loaded one.
 *
 * `enabled` lets callers skip the request (and the Hub round-trip) entirely
 * when messaging is out of scope — e.g. orgs without the messaging feature.
 */
const useMessagingOrganization = (enabled: Ref<boolean> | boolean = true) => {
    const { getOrganization } = useMessaging()

    return useQuery({
        queryKey: QUERY_KEYS.messagingOrganization(),
        queryFn: () => getOrganization(),
        enabled: computed(() => unref(enabled)),
        select: response => response.data,
        staleTime: TIME.Minute(5),
        // This gates access (nav visibility + the Inbox route guard); fail fast and
        // closed rather than retrying a failed entitlement lookup several times.
        retry: false
    })
}

export { useMessagingOrganization }
